import { z } from 'zod';
/**
 * Files (v2.0, Phase 17). Bounded impl: the file is uploaded as base64 JSON
 * (no multipart dep) and stored AES-256-GCM-encrypted inline. Object storage +
 * streamed multipart is the production path (docs/phase-17-files-plan.md).
 */
/**
 * ehr#209: a file's category, the same four the client portal's Documents offers (portal-credify
 * DOC_CATEGORIES), so staff and client uploads read alike.
 */
export const FILE_CATEGORIES = ['roi', 'records', 'labs', 'other'];
export const FILE_CATEGORY_LABELS = {
    roi: 'Release of info',
    records: 'Prior records',
    labs: 'Lab results',
    other: 'Other',
};
/** ehr#209: the optional note on an upload (PHI: stored encrypted). */
export const FILE_NOTE_MAX = 500;
/**
 * ehr#321 - the largest file: 4.5 MB. Files travel as base64 JSON, so that is exactly 6,000,000
 * characters (the schema below), inside the API's 6 MB body limit and nginx's 8 MB.
 */
export const FILE_MAX_BYTES = 4_500_000;
export const FILE_MAX_LABEL = '4.5 MB';
/**
 * ehr#321 - the file types a client chart accepts: standard document, spreadsheet and image
 * formats. Left out on purpose: anything a browser can run (HTML, SVG, XML, JavaScript),
 * macro-enabled Office files (.docm / .xlsm; a macro hidden in a .docx / .xlsx is refused by the
 * API too), programs, scripts and archives.
 */
export const ALLOWED_FILE_TYPES = [
    { ext: 'pdf', mime: 'application/pdf', kind: 'document' },
    { ext: 'doc', mime: 'application/msword', kind: 'document' },
    {
        ext: 'docx',
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        kind: 'document',
    },
    { ext: 'odt', mime: 'application/vnd.oasis.opendocument.text', kind: 'document' },
    { ext: 'rtf', mime: 'application/rtf', kind: 'document' },
    { ext: 'txt', mime: 'text/plain', kind: 'document' },
    { ext: 'xls', mime: 'application/vnd.ms-excel', kind: 'spreadsheet' },
    {
        ext: 'xlsx',
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        kind: 'spreadsheet',
    },
    { ext: 'ods', mime: 'application/vnd.oasis.opendocument.spreadsheet', kind: 'spreadsheet' },
    { ext: 'csv', mime: 'text/csv', kind: 'spreadsheet' },
    { ext: 'jpg', mime: 'image/jpeg', kind: 'image' },
    { ext: 'jpeg', mime: 'image/jpeg', kind: 'image' },
    { ext: 'png', mime: 'image/png', kind: 'image' },
    { ext: 'gif', mime: 'image/gif', kind: 'image' },
    { ext: 'webp', mime: 'image/webp', kind: 'image' },
    { ext: 'heic', mime: 'image/heic', kind: 'image' },
];
/** The file picker's `accept`, so the dialog offers only these. */
export const FILE_ACCEPT = ALLOWED_FILE_TYPES.map((t) => `.${t.ext}`).join(',');
export const ALLOWED_FILES_SUMMARY = 'PDF, Word, Excel, CSV, OpenDocument, RTF, text, or an image (JPG, PNG, GIF, WebP, HEIC)';
/** The last extension, lowercased ('' when there is none). */
export function fileExtension(name) {
    const base = name.trim().split(/[\\/]/).pop() ?? '';
    const dot = base.lastIndexOf('.');
    return dot > 0 && dot < base.length - 1 ? base.slice(dot + 1).toLowerCase() : '';
}
/** The allowed type for a file name, by its last extension; null when it isn't allowed. */
export function allowedFileType(name) {
    const ext = fileExtension(name);
    return ALLOWED_FILE_TYPES.find((t) => t.ext === ext) ?? null;
}
/**
 * A file name as it is stored: no folder part, control characters or invisible direction
 * markers (the right-to-left override that makes "report‮fdp.exe" read as "reportexe.pdf"),
 * no leading dots, single spaces, at most 200 characters with the extension kept.
 */
export function safeFileName(name) {
    const base = String(name ?? '')
        .split(/[\\/]/)
        .pop()
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u001f\u007f‎‏‪-‮⁦-⁩]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^\.+/, '');
    if (base.length <= 200)
        return base;
    const ext = fileExtension(base);
    return ext ? `${base.slice(0, 199 - ext.length)}.${ext}` : base.slice(0, 200);
}
/** Why a picked file can't be uploaded, or null when it can (the API checks the same, and more). */
export function fileUploadProblem(file, opts = {}) {
    const t = allowedFileType(file.name);
    if (!t || (opts.imageOnly && t.kind !== 'image'))
        return opts.imageOnly
            ? `${file.name} isn’t an image. Choose a JPG, PNG, GIF, WebP or HEIC.`
            : `${file.name} can’t be uploaded. Allowed: ${ALLOWED_FILES_SUMMARY}.`;
    if (file.size > FILE_MAX_BYTES)
        return `${file.name} is over ${FILE_MAX_LABEL} and can’t be uploaded.`;
    if (file.size === 0)
        return `${file.name} is empty.`;
    return null;
}
export const fileUploadSchema = z.object({
    name: z.string().min(1).max(255),
    mime: z.string().max(120).default('application/octet-stream'),
    dataBase64: z.string().min(1).max(6_000_000), // FILE_MAX_BYTES as base64
    category: z.enum(FILE_CATEGORIES).optional(),
    note: z.string().trim().max(FILE_NOTE_MAX).optional(),
});
/** alertshub#9: PATCH /files/:id/share - show the file in the client's portal (or stop showing it). */
export const fileShareSchema = z.object({ shared: z.boolean() });
//# sourceMappingURL=files.js.map